import { getReq } from '../service/API'
import useSWR from 'swr'
import { ENDPOINTS } from '../service/ENDPOINTS'
import { USER_TYPES, UserTypes } from '../Types'

export const NOTIFICATION_STATUS = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  CLICKED: 'CLICKED',
  SEEN: 'SEEN',
  DISMISSED: 'DISMISSED',
} as const

export type NOTIFICATION_STATUS_TYPE = (typeof NOTIFICATION_STATUS)[keyof typeof NOTIFICATION_STATUS]

export type NotificationConfig = {
  _id: string
  type: 'CONFIG'
  projectId: string
  triggerAction: string
  enabled: boolean
  resend: boolean
  resendIntervalHours: number
  recipientRoles: UserTypes[]
  content: {
    view: 'SUBJECT' | 'DUMMY_CHECK_BACKEMD_TYPE'
    banner: { title: string; body: string; button: string }
    email: { subject: string; body: string; linkTxt: string }
    target: { title: string; body: string }
  }
  createdAt: string
  updatedAt: string
}

export type NotificationType = {
  _id: string
  type: 'TARGET'
  projectId: string
  configId: NotificationConfig // populated; null if the config was deleted
  actionId: string
  actionType: string
  target: { subjectId: { siteId: string; displayId: string; _id: string } }
  recipient: { type: Exclude<UserTypes, 'TrialStaff'>; sub: string }
  status: NOTIFICATION_STATUS_TYPE
  token: string | null
  sentAt: string | null // ISO date of the (last) email send - resends refresh it
  seenAt: string | null // ISO date once SEEN
  linkClicked: string | null // ISO date once the email link is clicked
  resendAt: string | null
  createdAt: string
  updatedAt: string
}

const activeNotificationState: NOTIFICATION_STATUS_TYPE[] = [
  NOTIFICATION_STATUS.PENDING,
  NOTIFICATION_STATUS.SENT,
  NOTIFICATION_STATUS.CLICKED,
]

export const useNotifications = (projectId: string, allProjects: boolean = false, subjectId: string = '') => {
  const p = ENDPOINTS.PROJECTS.ME_NOTIFICATIONS()
  const { data, error, mutate } = useSWR(p, getReq, { suspense: true })

  const notifications: NotificationType[] = data?.data || []

  const activeNotifications: NotificationType[] = [...notifications].filter((n: NotificationType) => {
    const currentProject = !projectId || allProjects ? true : n.projectId === projectId
    const olderThan7days = new Date(n.createdAt).getTime() < new Date().getTime() - 7 * 24 * 60 * 60 * 1000
    return activeNotificationState.includes(n.status) && currentProject && !olderThan7days
  })

  const sortedActive = [...activeNotifications].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  // if subjectId is provided, filter notifications by subjectId
  const currentSubjectNotifications = subjectId
    ? sortedActive.filter((participant) => {
        return participant.target.subjectId._id === subjectId
      })
    : sortedActive

  const latestNotification = currentSubjectNotifications[0] || null

  return {
    latestNotification: latestNotification,
    activeNotifications: activeNotifications,
    data: !data ? data : notifications,
    isLoading: !error && !data,
    isError: error,
    mutate: mutate,
  }
}

export const notificationToLink = (notification: NotificationType) => {
  const type = notification.recipient.type

  const projectId = notification.projectId
  const siteId = notification.target.subjectId.siteId
  const targetId = notification.target.subjectId._id

  if (type === USER_TYPES.CTM || type === USER_TYPES.DM) {
    return `/project/${notification.projectId}/subject/view/${notification.target.subjectId._id}`
  }

  return notification.configId.content.view === 'SUBJECT'
    ? `/project/${projectId}/site/${siteId}/subject/view/${targetId}`
    : 'Not handled'
}
